import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Address from 'src/components/Address';
import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import ProposeNominatedTradeMutation from 'src/mutations/ProposeNominatedTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';

import FlatPricingForm from './TradeRuleProposeNominatedFlatPricingForm';
import TimeOfUsePricingForm from './TradeRuleProposeNominatedTimeOfUsePricingForm';

const TradeRuleProposeNominatedPropertyFragment = graphql`
  fragment TradeRuleProposeNominated_property on Property {
    id
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...TradeRuleProposeNominatedFlatPricingForm_property
    ...TradeRuleProposeNominatedTimeOfUsePricingForm_property
  }
`;

const mutationName = 'proposeNominatedTrade';

/**
 * TradeRuleProposeNominated component for proposing peer-to-peer trade rules
 * @param {object} props - Component props
 * @param {object} props.property - Property data from GraphQL
 * @param {object} props.router - Router object for navigation
 * @returns {React.ReactElement} TradeRuleProposeNominated component
 */
function TradeRuleProposeNominated(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  let { property } = props;
  const { router } = props;
  const intl = useIntl();

  property = useFragment(TradeRuleProposeNominatedPropertyFragment, property);

  const [processing, setProcessing] = useState(false);
  const [timeOfUsePricing, setTimeOfUsePricing] = useState(false);

  const onPricingButtonClick = () => {
    setTimeOfUsePricing(!timeOfUsePricing);
  };

  const handleTradeRuleProposeNominatedSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    const { id } = response.proposeNominatedTrade;
    const successMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.response_message.success', defaultMessage: 'A new peer-to-peer trade rule proposal has been sent with id {id}.' }, { id });

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      successMsg,
      mutationName,
    );

    router.push(`/properties/${property.id}/trade-rules/active`);
  };

  const handleTradeRuleProposeNominatedFailure = (error) => {
    setProcessing(false);

    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };

  const handleTradeRuleProposeNominated = (input) => {
    setProcessing(true);
    FlashesStore.reset();
    ProposeNominatedTradeMutation(
      input,
      handleTradeRuleProposeNominatedSuccess,
      handleTradeRuleProposeNominatedFailure,
    );
  };

  const allTradeRulesLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.all_trade_rules.label',
    defaultMessage: 'All trade rules',
  });
  const propertySettingsLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.property_settings.label',
    defaultMessage: 'Property Settings',
  });
  const propertiesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.breadcrumbs.properties.label',
    defaultMessage: ' Properties',
  });
  const tradeRulesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.breadcrumbs.trade_rules.label',
    defaultMessage: 'Trade Rules',
  });
  const proposePeerToPeerBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.breadcrumbs.propose_peer_to_peer_trade_rule.label',
    defaultMessage: 'Propose peer-to-peer trade rule',
  });
  const tradeRuleProposeContextLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_peer_to_peer.title',
    defaultMessage: 'Trade Rule - Propose Peer-to-Peer',
  });

  const renderPropertyControls = () => (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link
          to={`/properties/${property.id}/trade-rules`}
          className="btn btn-darken"
        >
          {allTradeRulesLabel}
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link
          to={`/properties/${property.id}/settings`}
          className="btn btn-darken"
        >
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  if (!property) {
    return <Loading />;
  }

  const { address, id, title } = property;
  const addr = new Address(address);
  const pageTitle = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.page.title',
    defaultMessage: 'Enosi - Property: {title} - Propose peer-to-peer trade rule',
  }, { title });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          {
            name: propertiesBreadcrumbLabel,
            path: '/properties',
          },
          { name: title, path: `/properties/${id}` },
          {
            name: tradeRulesBreadcrumbLabel,
            path: `/properties/${id}/trade-rules/active`,
          },
          { name: proposePeerToPeerBreadcrumbLabel },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr.simpleString()}
        context={tradeRuleProposeContextLabel}
        controls={renderPropertyControls()}
      />
      {timeOfUsePricing
        ? (
          <TimeOfUsePricingForm
            property={property}
            handleTradeRuleProposeNominated={handleTradeRuleProposeNominated}
            processing={processing}
            router={router}
            onPricingButtonClick={onPricingButtonClick}
          />
        )
        : (
          <FlatPricingForm
            property={property}
            handleTradeRuleProposeNominated={handleTradeRuleProposeNominated}
            processing={processing}
            router={router}
            onPricingButtonClick={onPricingButtonClick}
          />
        )}
    </>
  );
}

TradeRuleProposeNominated.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

TradeRuleProposeNominated.defaultProps = {
  property: null,
};

export default TradeRuleProposeNominated;
